import React, { useEffect, useState, useRef } from 'react';
import { Box, List, ListItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import BoltIcon from '@mui/icons-material/Bolt';
import {
  formatearConsumo,
  getColorForConsumo,
  mapaDispositivos,
  DispositivoConConsumo,
  mapaHabitaciones,
  mapaTableros,
} from '../services/consumptionService';
import { eventBus } from '../services/EventBus';
import { SOLAR_INSTALLED, getLastSolarBatteryPowerW, getLastSolarPvTotalPowerW, SOLAR_BATTERY_POWER_EVENT, SOLAR_PV_TOTAL_POWER_EVENT } from '../installed/solar';
import { formatPower } from '../utils/formatPower';
import { getColorForTipoArtefacto } from './shared/DeviceTypeImage';
import DeviceTypeImageSmall from './shared/DeviceTypeImageSmall';
import { getConsumptionCorrectionWatts } from '../services/api';

interface DeviceListProps {
  isAdmin?: boolean;
  selectedHabitacion?: number | null;
  roomMatrixView: boolean;
  filteredDevices?: any[];
  isSettingsPage?: boolean;
  onTotalClick?: () => void;
  onOfflineClick?: () => void;
  nombreHabitacion?: string;
  isDashboardView?: boolean;
  isTodosTab?: boolean; // Nueva prop para detectar tab "Todos"
  showOnlyTop10?: boolean; // Nueva prop para mostrar solo los top 10 consumidores
  showOnlyTop15?: boolean; // Nueva prop para mostrar solo los top 15 consumidores
  hideOfflineAndTotal?: boolean; // Nueva prop para ocultar recuadros Offline y Con Consumo
  excludeArtifactTypes?: string[]; // Tipos de artefacto a excluir
  includeSpecialRooms?: boolean; // Nueva prop para incluir habitaciones especiales (desactivar filtrado)
  excludeSpecialRoomNames?: string[]; // Nombres de habitaciones especiales a excluir específicamente
}

const DeviceList: React.FC<DeviceListProps> = ({ 
  isAdmin = false,
  selectedHabitacion = null,
  roomMatrixView,
  filteredDevices,
  isSettingsPage = false,
  onTotalClick,
  onOfflineClick,
  nombreHabitacion = '',
  isDashboardView = false,
  isTodosTab = false, // Nueva prop para detectar tab "Todos"
  showOnlyTop10 = false, // Nueva prop para mostrar solo los top 10 consumidores
  showOnlyTop15 = false, // Nueva prop para mostrar solo los top 15 consumidores
  hideOfflineAndTotal = false, // Nueva prop para ocultar recuadros Offline y Con Consumo
  excludeArtifactTypes = [], // Tipos de artefacto a excluir
  includeSpecialRooms = false, // Nueva prop para incluir habitaciones especiales (desactivar filtrado)
  excludeSpecialRoomNames = [], // Nombres de habitaciones especiales a excluir específicamente
}) => {
  const { t } = useTranslation();
  const [dispositivos, setDispositivos] = useState<DispositivoConConsumo[]>([]);
  const [dispositivosOffline, setDispositivosOffline] = useState(0);
  const [top10Ids, setTop10Ids] = useState<number[]>([]);
  const [habitaciones, setHabitaciones] = useState<any[]>([]);
  const [batteryPowerW, setBatteryPowerW] = useState<number | null>(() => getLastSolarBatteryPowerW());
  const [pvTotalPowerW, setPvTotalPowerW] = useState<number | null>(() => getLastSolarPvTotalPowerW());
  /** Factor de corrección (W) del recuadro Consumo; default histórico 450. */
  const [consumptionCorrectionW, setConsumptionCorrectionW] = useState(450);

  const selectedHabitacionRef = useRef<number | null>(selectedHabitacion);
  const roomMatrixViewRef = useRef<boolean>(roomMatrixView);

  const habitacionesLength = Object.values(mapaHabitaciones).length;

  useEffect(() => {
    selectedHabitacionRef.current = selectedHabitacion;
    roomMatrixViewRef.current = roomMatrixView;
  }, [selectedHabitacion, roomMatrixView]);

  useEffect(() => {
    console.log('🔧 [DeviceList] useEffect ejecutándose - suscribiéndose a eventos');
    function actualizarDesdeMemoria() {
      // CRÍTICO: Leer directamente de mapaDispositivos en el momento de la actualización
      // para obtener los valores más recientes
      const selectedHabitacionActual = selectedHabitacionRef.current;
      void roomMatrixViewRef.current;
      
      // CRÍTICO: Crear un nuevo array con nuevos objetos para que React detecte el cambio
      let dispositivosData = Object.values(mapaDispositivos).map(d => ({ ...d }));
      
      const mostrarDispositivosHabitacion = selectedHabitacionActual !== null;
      if (mostrarDispositivosHabitacion && selectedHabitacionActual) {
        dispositivosData = dispositivosData.filter(d => d.habitacion_id === selectedHabitacionActual);
      }
      
      // CRÍTICO: Siempre actualizar para reflejar cambios de estado (online/offline, consumo, etc.)
      // Usar función de actualización para forzar re-render incluso si la referencia es la misma
      setDispositivos(prev => {
        // Comparar por longitud y IDs para detectar cambios
        if (prev.length !== dispositivosData.length) {
          return dispositivosData;
        }
        // Verificar si algún dispositivo cambió comparando propiedades clave
        const hasChanges = prev.some((prevDev, idx) => {
          const newDev = dispositivosData[idx];
          return !newDev || 
                 prevDev.id !== newDev.id ||
                 prevDev.estado !== newDev.estado ||
                 prevDev.online !== newDev.online ||
                 prevDev.consumo !== newDev.consumo;
        });
        
        if (hasChanges) {
          return dispositivosData;
        }
        
        // Si no hay cambios detectados, aún así actualizar para asegurar sincronización
        return dispositivosData;
      });
    }
    actualizarDesdeMemoria();
    // Suscribirse a eventos
    const unsubEstructura = eventBus.on('estructura:actualizada', () => {
      console.log('🔧 [DeviceList] Evento estructura:actualizada recibido');
      actualizarDesdeMemoria();
    });
    const unsubDispositivo = eventBus.on('dispositivo:actualizado', () => {
      console.log('🔧 [DeviceList] Evento dispositivo:actualizado recibido');
      actualizarDesdeMemoria();
    });
    // CRÍTICO: También escuchar device:update que es el evento principal de actualización
    const unsubDeviceUpdate = eventBus.on('device:update', () => {
      console.log('🔧 [DeviceList] Evento device:update recibido');
      actualizarDesdeMemoria();
    });
    const unsubRoomAssigned = eventBus.on('room:assigned', (data) => {
      console.log('🔧 [DeviceList] Evento room:assigned recibido:', data);
      actualizarDesdeMemoria();
    });
    
    return () => {
      unsubEstructura();
      unsubDispositivo();
      unsubDeviceUpdate();
      unsubRoomAssigned();
    };
  }, []); // Eliminada la dependencia que causaba el bucle infinito

  useEffect(() => {
    const habitacionesData = Object.values(mapaHabitaciones).map(h => h.datos);
    console.log(`[FILTRO] Habitaciones cargadas:`, habitacionesData);
    console.log(`[FILTRO] Mapa de tableros:`, mapaTableros);
    setHabitaciones(habitacionesData);
  }, [habitacionesLength]);

  // Estilos para el scroll-bar (solo para el contenido scrolleable)
  const scrollableContentStyle = {
    height: '100%',
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
    scrollbarWidth: 'thin' as const,
    scrollbarColor: '#2391FF #1a1a1a',
    touchAction: 'pan-y' as const, // ✅ Permitir scroll vertical en móviles
    WebkitOverflowScrolling: 'touch' as const,
    // Asegurar que el scroll funcione desde cualquier parte
    overscrollBehavior: 'contain' as const,
    // CRÍTICO: Asegurar que los eventos de scroll funcionen
    pointerEvents: 'auto' as const,
    // Prevenir interferencias con el scroll
    willChange: 'scroll-position' as const,
    // Asegurar que el scroll funcione con mouse wheel
    scrollBehavior: 'smooth' as const,
    '&::-webkit-scrollbar': {
      width: '3px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#1a1a1a',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#2391FF',
      borderRadius: '2px',
    },
  };

  // Determinar si debemos mostrar los dispositivos de una habitación específica
  const mostrarDispositivosHabitacion = selectedHabitacion !== null;
  
  // Debug: Log controlado solo cuando se abre el modal
  // useEffect(() => {
  //   if (isDashboardView && roomMatrixView) {
  // console.log(`[FILTRO DEBUG] Modal abierto - Parámetros:`, {
  // // // isAdmin,
  // // // selectedHabitacion,
  // // // roomMatrixView,
  // // // isDashboardView,
  // // // isSettingsPage,
  // // // mostrarDispositivosHabitacion
  // // // });
      
  // console.log(`[FILTRO DEBUG] Mapas globales:`, {
  // // // totalDispositivos: Object.keys(mapaDispositivos).length,
  // // // totalHabitaciones: Object.keys(mapaHabitaciones).length,
  // // // totalTableros: Object.keys(mapaTableros).length,
  // // // dispositivos: dispositivos.length
  // // // });
      
  // console.log(`[FILTRO DEBUG] Tableros cargados:`, Object.values(mapaTableros).map(t => ({
  // // // id: t.id,
  // // // nombre: t.nombre,
  // // // habitaciones: t.habitaciones
  // // // })));
      
  // console.log(`[FILTRO DEBUG] Habitaciones cargadas:`, habitaciones.map(h => ({
  // // // id: h.id,
  // // // nombre: h.nombre
  // // // })));
  //   }
  // }, [isDashboardView, roomMatrixView]); // Solo se ejecuta cuando cambian estos parámetros

  // Obtener IDs de habitaciones especiales directamente de los mapas globales
  const habitacionesEspeciales = React.useMemo(() => {
    let conexion_red: number | undefined = undefined;
    let consumo_total: number | undefined = undefined;
    
    // Buscar en todas las habitaciones por nombre
    Object.values(mapaHabitaciones).forEach(habitacion => {
      const nombre = habitacion.datos.nombre.toLowerCase();
      if (nombre.includes('conexion') && nombre.includes('red')) {
        conexion_red = habitacion.datos.id;
      } else if (nombre.includes('consumo') && nombre.includes('total')) {
        consumo_total = habitacion.datos.id;
      }
    });
    
    return { conexion_red, consumo_total };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mapa global mutado; el tamaño refleja cambios relevantes
  }, [habitacionesLength]);

  // Usar los IDs de habitaciones especiales
  const idConexionRed = habitacionesEspeciales.conexion_red || null;
  const idConsumoTotal = habitacionesEspeciales.consumo_total || null;
  
  

  // Estado para forzar recálculo cuando cambien los consumos (sin cambiar el tamaño del mapa)
  const [, setConsumoUpdateTrigger] = useState(0);

  // Escuchar actualizaciones de dispositivos para forzar recálculo de consumos
  useEffect(() => {
    const handleDeviceUpdate = () => {
      setConsumoUpdateTrigger(prev => prev + 1);
    };
    
    const unsubDispositivo = eventBus.on('dispositivo:actualizado', handleDeviceUpdate);
    const unsubDeviceUpdate = eventBus.on('device:update', handleDeviceUpdate);
    const unsubEstructura = eventBus.on('estructura:actualizada', handleDeviceUpdate);
    
    return () => {
      unsubDispositivo();
      unsubDeviceUpdate();
      unsubEstructura();
    };
  }, []);

  useEffect(() => {
    const unsub = eventBus.on(SOLAR_BATTERY_POWER_EVENT, (watts: number) => {
      if (typeof watts === 'number' && !Number.isNaN(watts)) {
        setBatteryPowerW(watts);
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = eventBus.on(SOLAR_PV_TOTAL_POWER_EVENT, (watts: number) => {
      if (typeof watts === 'number' && !Number.isNaN(watts)) {
        setPvTotalPowerW(watts);
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getConsumptionCorrectionWatts();
        if (!cancelled && typeof res?.correction_watts === 'number') {
          setConsumptionCorrectionW(res.correction_watts);
        }
      } catch {
        /* conservar default 450 */
      }
    })();
    const unsub = eventBus.on('consumption-correction:updated', (watts: number) => {
      if (typeof watts === 'number' && !Number.isNaN(watts)) {
        setConsumptionCorrectionW(watts);
      }
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  // Identificar habitaciones que pertenecen a tableros "Tableros" y "Consumos"
  const habitacionesExcluidas = new Set<number>();
  
  // Solo excluir habitaciones especiales si includeSpecialRooms es false
  if (!includeSpecialRooms) {
    // Agregar habitaciones especiales
    if (idConexionRed !== null) {
      habitacionesExcluidas.add(idConexionRed);
      console.log(`[FILTRO] Habitación especial Conexión a Red excluida: ${idConexionRed}`);
    }
    if (idConsumoTotal !== null) {
      habitacionesExcluidas.add(idConsumoTotal);
      console.log(`[FILTRO] Habitación especial Consumo Total excluida: ${idConsumoTotal}`);
    }
    
    // Log temporal para ver todos los tableros disponibles
    console.log(`[TEMP] Tableros disponibles:`, Object.values(mapaTableros).map(t => `"${t.nombre}"`));
    
    // Agregar habitaciones de tableros "Tableros" y "Consumos"
    habitaciones.forEach(habitacion => {
      // Buscar el tablero de esta habitación
      const tablero = Object.values(mapaTableros).find(t => 
        t.habitaciones.includes(habitacion.id)
      );
      
      // Log temporal para ver nombres de tableros
      if (tablero && (tablero.nombre === "Tableros" || tablero.nombre === "Consumos")) {
        habitacionesExcluidas.add(habitacion.id);
        console.log(`[TEMP] Habitación ${habitacion.nombre} (ID: ${habitacion.id}) excluida por tablero: "${tablero.nombre}"`);
      }
      
      // Log temporal para ver todos los tableros
      // if (tablero) {
      // console.log(`[TEMP] Habitación ${habitacion.nombre} (ID: ${habitacion.id}) pertenece al tablero: "${tablero.nombre}"`);
      // }
    });
  }
  
  console.log(`[FILTRO DEBUG] Total habitaciones excluidas: ${habitacionesExcluidas.size}`);
  console.log(`[FILTRO DEBUG] Habitaciones excluidas:`, Array.from(habitacionesExcluidas));

  // Lista visible: excluir dispositivos de habitaciones especiales y tableros específicos
  const dispositivosVisibles = dispositivos.filter(d => {
    console.log(`[FILTRO] Evaluando dispositivo: ${d.nombre} (ID: ${d.id}, Habitación: ${d.habitacion_id}, Consumo: ${d.consumo})`);
    
    // Si estamos en la página de Settings, mostrar todos los dispositivos
    if (isSettingsPage) {
      console.log(`[FILTRO] Dispositivo ${d.nombre} incluido por Settings`);
      return true;
    }
    
    // Si estamos en Dashboard, filtrar dispositivos con consumo = 0W
    if (isDashboardView && (d.consumo || 0) === 0) {
      console.log(`[FILTRO] Dispositivo ${d.nombre} filtrado por consumo = 0W: ${d.consumo}`);
      return false;
    }
    
    // Si estamos en vista de habitación, mostrar SOLO los dispositivos de esa habitación
    if (mostrarDispositivosHabitacion && selectedHabitacion !== null) {
      const incluir = d.habitacion_id === selectedHabitacion;
      console.log(`[FILTRO] Dispositivo ${d.nombre} ${incluir ? 'incluido' : 'filtrado'} por vista de habitación: ${d.habitacion_id} === ${selectedHabitacion}`);
      return incluir;
    }
    
    // En vistas globales, excluir dispositivos de habitaciones excluidas
    // Y también excluir dispositivos de habitaciones especiales si el usuario no es admin
    // PERO solo si includeSpecialRooms es false
    const esHabitacionExcluida = !includeSpecialRooms && habitacionesExcluidas.has(d.habitacion_id || 0);
    const esHabitacionEspecial = !includeSpecialRooms && !isAdmin && d.es_habitacion_especial;
    
    if (esHabitacionExcluida) {
      console.log(`[FILTRO] Dispositivo ${d.nombre} filtrado por habitación excluida: ${d.habitacion_id}`);
    }
    if (esHabitacionEspecial) {
      console.log(`[FILTRO] Dispositivo ${d.nombre} filtrado por habitación especial: ${d.habitacion_id}`);
    }
    
    const incluir = !esHabitacionExcluida && !esHabitacionEspecial;
    console.log(`[FILTRO] Dispositivo ${d.nombre} ${incluir ? 'incluido' : 'filtrado'} por filtros generales`);
    return incluir;
  });

  // Calcular el consumo total de los dispositivos en la habitación Conexion a Red usando la lista interna
  // IMPORTANTE: Recalcular directamente desde mapaDispositivos para obtener valores actualizados
  const consumoConexionRed =
    idConexionRed === null
      ? 0
      : Object.values(mapaDispositivos)
          .filter(d => d.habitacion_id === idConexionRed)
          .reduce((total, d) => total + (d.consumo || 0), 0);

  // Calcular el consumo total de los dispositivos en la habitación Consumo Total usando la lista interna
  // IMPORTANTE: Recalcular directamente desde mapaDispositivos para obtener valores actualizados
  const consumoTotalHabitacion =
    idConsumoTotal === null
      ? 0
      : Object.values(mapaDispositivos)
          .filter(d => d.habitacion_id === idConsumoTotal)
          .reduce((total, d) => total + (d.consumo || 0), 0);
  
  
  

  // Sumar factor de corrección configurable (Ajustes generales) al consumo total
  const consumoTotalConInversores = React.useMemo(() => {
    return consumoTotalHabitacion + consumptionCorrectionW;
  }, [consumoTotalHabitacion, consumptionCorrectionW]);

  // Lógica robusta para detectar la vista
  const esVistaGlobal = !roomMatrixView && (selectedHabitacion === null || selectedHabitacion === undefined);
  const esVistaMatriz = roomMatrixView === true && (selectedHabitacion === null || selectedHabitacion === undefined);
  const esVistaHabitacion = selectedHabitacion !== null && selectedHabitacion !== undefined;

  // Calcular el valor a mostrar en el recuadro de arriba
  let consumoAMostrar = 0;
  if (esVistaGlobal || esVistaMatriz) {
    consumoAMostrar = consumoConexionRed;
  } else if (esVistaHabitacion) {
    // Sumar solo los dispositivos de la habitación seleccionada
    consumoAMostrar = dispositivosVisibles.reduce((total, d) => total + (d.consumo || 0), 0);
  }



  // Calcular el total de dispositivos según la vista
  let totalDispositivosAMostrar = 0;
  // Siempre contar solo los dispositivos que realmente se muestran en la lista
  totalDispositivosAMostrar = dispositivosVisibles.length;

  const [onlineStatesUpdateTrigger, setOnlineStatesUpdateTrigger] = React.useState(0);

  // Calcular el total global de dispositivos offline (sin importar filtros ni vista)
  const totalDispositivosOfflineGlobal = React.useMemo(
    () => Object.values(mapaDispositivos).filter(d => !d.online).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mapaDispositivos es mutado in-place; el trigger viene del eventBus
    [onlineStatesUpdateTrigger]
  );

  // Escuchar eventos de actualización de dispositivos para recalcular el contador offline
  React.useEffect(() => {
    const handleDeviceUpdate = (data: any) => {
      // Si el evento incluye información sobre el estado online, forzar recálculo
      if (data && (data.online !== undefined || data.id)) {
        setOnlineStatesUpdateTrigger(prev => prev + 1);
      }
    };

    const unsubDeviceUpdate = eventBus.on('device:update', handleDeviceUpdate);
    const unsubDispositivo = eventBus.on('dispositivo:actualizado', handleDeviceUpdate);

    return () => {
      unsubDeviceUpdate();
      unsubDispositivo();
    };
  }, []);

  // Usar siempre el total global para el recuadro de Offline
  useEffect(() => {
    setDispositivosOffline(totalDispositivosOfflineGlobal);
  }, [totalDispositivosOfflineGlobal]);

  // Mapear dispositivos con el nombre de la habitación y tipo_artefacto (usando la lista visible)
  const dispositivosConNombreHabitacion = dispositivosVisibles.map(d => {
    const habitacion = habitaciones.find(h => h.id === d.habitacion_id);
    // Obtener tipo_artefacto desde mapaDispositivos (fuente de verdad actualizada)
    const mapaDevice = mapaDispositivos[d.id];
    const tipoArtefacto = d.tipo_artefacto || mapaDevice?.tipo_artefacto || (window as any).mapaDispositivos?.[d.id]?.tipo_artefacto || null;
    return {
      ...d,
      habitacion_nombre: habitacion ? habitacion.nombre : undefined,
      tipo_artefacto: tipoArtefacto
    };
  });

  const formattedConsumo = formatearConsumo(consumoAMostrar);
  
  // Usar la función centralizada para determinar el color del consumo
  const colorValorConsumo = getColorForConsumo(consumoAMostrar);
  
  // Justo antes del render, determina el label a mostrar:
  let consumoLabel = '';
  if (esVistaHabitacion) {
    consumoLabel = nombreHabitacion || (typeof selectedHabitacion === 'number' ? mapaHabitaciones[selectedHabitacion]?.datos?.nombre : t('common.room'));
  } else {
    consumoLabel = (consumoAMostrar >= 0 ? t('devices.consumingFromNetwork') : t('devices.deliveringToNetwork'));
  }

  // Memoizar dispositivos ordenados para evitar recreaciones innecesarias
  const dispositivosOrdenados = React.useMemo(() => {
    // Filtrar dispositivos excluyendo tipos de artefacto y habitaciones especificadas
    let filtrados = [...dispositivosConNombreHabitacion];
    
    // Filtrar por tipo de artefacto
    if (excludeArtifactTypes.length > 0) {
      filtrados = filtrados.filter(dispositivo => {
        const tipoArtefacto = dispositivo.tipo_artefacto?.toLowerCase() || '';
        return !excludeArtifactTypes.some(excluido => 
          tipoArtefacto.includes(excluido.toLowerCase())
        );
      });
    }
    
    // Filtrar por nombres de habitaciones especiales (si se especifican)
    if (excludeSpecialRoomNames.length > 0) {
      filtrados = filtrados.filter(dispositivo => {
        const habitacionNombre = dispositivo.habitacion_nombre?.toLowerCase() || '';
        return !excludeSpecialRoomNames.some(excluido => 
          habitacionNombre.includes(excluido.toLowerCase())
        );
      });
    }
    
    // Ordenar por consumo absoluto
    const ordenados = filtrados.sort((a, b) => Math.abs(b.consumo) - Math.abs(a.consumo));
    
    // Si showOnlyTop15 es true, solo mostrar los primeros 15
    if (showOnlyTop15) {
      return ordenados.slice(0, 15);
    }
    
    // Si showOnlyTop10 es true, solo mostrar los primeros 10
    if (showOnlyTop10) {
      return ordenados.slice(0, 10);
    }
    
    return ordenados;
  }, [dispositivosConNombreHabitacion, showOnlyTop10, showOnlyTop15, excludeArtifactTypes, excludeSpecialRoomNames]);

  // Actualizar top 10 basado en dispositivos visibles
  useEffect(() => {
    const nuevosTop10Ids = dispositivosOrdenados
      .slice(0, 10)
      .map(d => d.id);
    setTop10Ids(nuevosTop10Ids);
  }, [dispositivosOrdenados]);

  return (
    <Box className="device-list" sx={{ 
      backgroundColor: '#1a1a1a', 
      borderRadius: '10px', // 10px
      color: 'white', 
      width: '98%', // 98% del ancho del modal
      margin: '0 auto', // Centrado
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 0, // Importante para que flex funcione correctamente
      overflow: 'hidden', // Prevenir overflow del contenedor principal
    }}>
      {/* Header fijo con los 5 recuadros */}
      <Box sx={{ 
        p: 1,
        borderBottom: '1px solid #1a1a1a',
        backgroundColor: '#1a1a1a',
        flexShrink: 0, // No se encoge
        borderTopLeftRadius: '10px',
        borderTopRightRadius: '10px',
        // CRÍTICO: No bloquear eventos de scroll del contenido debajo
        pointerEvents: 'auto',
        touchAction: 'manipulation', // Solo permitir manipulación en el header, no scroll
      }}>
        {/* Recuadro principal */}
        <Box sx={{ 
          backgroundColor: '#333',
          borderRadius: '10px', 
          padding: '8px',
          textAlign: 'center', 
          mb: 1,
          height: 'auto',
        }}>
          {/* Primer renglón: nombre de habitación o estado de red */}
          <Typography sx={{ 
            fontSize: '0.95rem', 
            fontWeight: 'bold',
            mb: 0.5,
            color: 'white'
          }}>
            {consumoLabel}
          </Typography>
          
          {/* Segundo renglón: ícono y valor de consumo */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <BoltIcon sx={{ 
              color: colorValorConsumo,
              mr: 0.5 
            }} />
            <Typography sx={{ 
              color: colorValorConsumo, 
              fontSize: '1rem', 
              fontWeight: 'bold' 
            }}>
              {formattedConsumo}
            </Typography>
          </Box>
        </Box>
        
        {/* Mostrar los recuadros de total y offline solo si no estamos en vista de habitación */}
        {!mostrarDispositivosHabitacion && (
          <>
            {/* Consumo (total), generación (autogen) y baterías — misma fila */}
            <Box sx={{ display: 'flex', gap: 0.5, mb: 1, alignItems: 'stretch' }}>
              <Box sx={{ flex: 1, minWidth: 0, backgroundColor: '#333', borderRadius: '8px', padding: '6px 4px', textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 'bold', color: getColorForConsumo(consumoTotalConInversores), lineHeight: 1.15 }}>
                  {formatearConsumo(consumoTotalConInversores)}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 'bold', mt: 0.25 }}>{t('devices.consumption')}</Typography>
              </Box>
              {SOLAR_INSTALLED && <Box sx={{ flex: 1, minWidth: 0, backgroundColor: '#333', borderRadius: '8px', padding: '6px 4px', textAlign: 'center' }}>
                <Typography sx={{
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  color: pvTotalPowerW === null ? '#888888' : '#2391FF',
                  lineHeight: 1.15,
                }}>
                  {pvTotalPowerW === null ? '—' : formatPower(pvTotalPowerW)}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 'bold', mt: 0.25 }}>{t('devices.generation')}</Typography>
              </Box>}
              {SOLAR_INSTALLED && <Box sx={{ flex: 1, minWidth: 0, backgroundColor: '#333', borderRadius: '8px', padding: '6px 4px', textAlign: 'center' }}>
                <Typography sx={{
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  color: batteryPowerW === null ? '#888888' : getColorForConsumo(batteryPowerW),
                  lineHeight: 1.15,
                }}>
                  {batteryPowerW === null ? '—' : formatearConsumo(batteryPowerW)}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 'bold', mt: 0.25 }}>{t('devices.batteries')}</Typography>
              </Box>}
            </Box>

            {/* Recuadros existentes de offline y total - Ocultar si hideOfflineAndTotal es true */}
            {!hideOfflineAndTotal && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '0.5px' }}>
                <Box 
                  sx={{ backgroundColor: '#333', borderRadius: '8px', padding: '8px', textAlign: 'center', width: '45%', cursor: onOfflineClick ? 'pointer' : 'default' }}
                  onClick={onOfflineClick}
                >
                  <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>{dispositivosOffline}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Offline</Typography>
                </Box>
                <Box 
                  sx={{ backgroundColor: '#333', borderRadius: '8px', padding: '8px', textAlign: 'center', width: '45%', cursor: onTotalClick ? 'pointer' : 'default' }}
                  onClick={onTotalClick}
                >
                  <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>{totalDispositivosAMostrar}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                    {(esVistaMatriz && !isSettingsPage) || isTodosTab ? t('devices.withConsumption') : 'Total'}
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
      
      {/* Contenido scrolleable - Lista de dispositivos */}
      <Box 
        data-scrollable-content
        sx={{ 
          flex: 1,
          overflow: 'hidden',
          minHeight: 0, // Importante para que flex funcione correctamente
          position: 'relative', // Asegurar contexto de apilamiento
          zIndex: 10, // Asegurar que esté sobre otros elementos
          // Asegurar que el scroll funcione desde cualquier parte
          cursor: 'default',
          ...scrollableContentStyle
        }}
      >
      
      <List 
        sx={{ 
          padding: 0,
          touchAction: 'pan-y', // Permitir scroll vertical en toda la lista
          WebkitOverflowScrolling: 'touch',
          // CRÍTICO: Asegurar que los eventos de scroll funcionen
          pointerEvents: 'auto',
          // Asegurar que el scroll funcione desde cualquier parte
          cursor: 'default',
        }}
      >
        {dispositivosOrdenados.map((dispositivo) => {
          const isTop10 = top10Ids.includes(dispositivo.id);
          
          return (
            <ListItem 
              key={dispositivo.id} 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                py: 0.2, 
                px: 0.5,
                touchAction: 'pan-y', // Permitir scroll vertical
                userSelect: 'none', // Prevenir selección de texto que puede interferir con scroll
                // CRÍTICO: No bloquear eventos de scroll
                pointerEvents: 'auto',
                // Prevenir que el ListItem capture eventos de scroll
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
                // Asegurar que el scroll funcione desde cualquier parte del item
                WebkitTouchCallout: 'none',
              }} 
              className="device-list-item"
            >
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  flex: 1, 
                  minWidth: 0,
                  // Permitir scroll pasando a través de este elemento cuando no hay interacción
                  touchAction: 'pan-y',
                }}
              >
                {/* Imagen del dispositivo - Usa DeviceTypeImage con lógica de tipo_artefacto */}
                <Box 
                  sx={{ 
                    mr: 1, 
                    flexShrink: 0,
                    cursor: isAdmin && dispositivo.ip ? 'pointer' : 'default',
                    // Solo permitir eventos en la imagen cuando es clickeable
                    pointerEvents: isAdmin && dispositivo.ip ? 'auto' : 'auto',
                    touchAction: isAdmin && dispositivo.ip ? 'manipulation' : 'pan-y',
                  }}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevenir que el evento se propague
                    if (isAdmin && dispositivo.ip) {
                      window.open(`http://${dispositivo.ip}/#/`, '_blank', 'width=1600,height=1200,left=100,top=100');
                    }
                  }}
                  onMouseDown={(e) => {
                    // No prevenir el comportamiento por defecto para permitir scroll
                    if (!isAdmin || !dispositivo.ip) {
                      e.stopPropagation();
                    }
                  }}
                >
                  <DeviceTypeImageSmall
                    tipo_artefacto={dispositivo.tipo_artefacto}
                    deviceIP={dispositivo.ip}
                    isAdmin={isAdmin}
                    onDeviceClick={isAdmin && dispositivo.ip ? (ip) => {
                      window.open(`http://${ip}/#/`, '_blank', 'width=1600,height=1200,left=100,top=100');
                    } : undefined}
                  />
                </Box>
                
                {/* Nombre del dispositivo */}
                <Typography 
                  sx={{ 
                    fontSize: '0.75rem', 
                    flexShrink: 1, 
                    whiteSpace: 'nowrap', 
                    textOverflow: 'ellipsis', 
                    overflow: 'hidden', 
                    flex: 1,
                    fontWeight: isTop10 ? 'bold' : 'normal', // Nombre en negrita para top 10
                    // CRÍTICO: Permitir que los eventos de scroll pasen
                    pointerEvents: 'none', // No capturar eventos de mouse/touch
                    touchAction: 'pan-y',
                    userSelect: 'none',
                  }}
                >
                  {dispositivo.nombre}
                </Typography>
              </Box>
              
              {/* Tipo de artefacto alineado a la derecha antes del consumo */}
              {dispositivo.tipo_artefacto && (
                <Typography 
                  sx={{ 
                    fontSize: '0.5625rem', // 3 puntos más chico (0.75rem - 0.1875rem = 0.5625rem, aprox 9px)
                    color: getColorForTipoArtefacto(dispositivo.tipo_artefacto),
                    flexShrink: 0, 
                    whiteSpace: 'nowrap', 
                    mr: '8px', // Espacio antes del consumo
                    fontWeight: isTop10 ? 'bold' : 'normal',
                    // CRÍTICO: Permitir que los eventos de scroll pasen
                    pointerEvents: 'none',
                    touchAction: 'pan-y',
                    userSelect: 'none',
                  }}
                >
                  {dispositivo.tipo_artefacto}
                </Typography>
              )}
              
              <Typography 
                sx={{ 
                  fontSize: '0.75rem', 
                  color: getColorForConsumo(dispositivo.consumo), 
                  flexShrink: 0, 
                  whiteSpace: 'nowrap', 
                  textOverflow: 'ellipsis', 
                  overflow: 'hidden', 
                  ml: '2px',
                  mr: '2px', // Separar del borde derecho
                  fontWeight: isTop10 ? 'bold' : 'normal', // Consumo en negrita para top 10
                  // CRÍTICO: Permitir que los eventos de scroll pasen
                  pointerEvents: 'none',
                  touchAction: 'pan-y',
                  userSelect: 'none',
                }}
              >
                {formatearConsumo(dispositivo.consumo)}
              </Typography>
            </ListItem>
          );
        })}
        
        {dispositivos.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#888' }}>
              {t('devices.noDevicesAvailable')}
            </Typography>
            {!isAdmin && (
              <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 1 }}>
                {t('devices.noRoomAccess')}
              </Typography>
            )}
          </Box>
        )}
      </List>
      </Box>
    </Box>
  );
};

export default DeviceList;
